"use client";

import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { AllBenchmarkListItem } from "@/type/commonTypes";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

type SelectBenchmarkDropdownProps = {
  lists: AllBenchmarkListItem[];
  onChange?: (items: AllBenchmarkListItem[]) => void;
};

export default function SelectBenchmarkDropdown({ lists, onChange }: SelectBenchmarkDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const [selectedItems, setSelectedItems] = useState<AllBenchmarkListItem[]>([]);
  const [sortedList, setSortedList] = useState<AllBenchmarkListItem[]>(lists);
  const [isApplied, setIsApplied] = useState(false);

  const [tempSelectedItems, setTempSelectedItems] = useState<AllBenchmarkListItem[]>([]);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setSortedList(lists);
  }, [lists]);

  const handleOpen = () => {
    setTempSelectedItems(selectedItems);
    setSearch("");
    setIsOpen((v)=>!v);
  };

  const handleClose = () => {
    setTempSelectedItems(selectedItems);
    setSearch("");
    setIsOpen(false);
  };

  const toggleSelect = (item: AllBenchmarkListItem) => {
    if (!item.ind_id) return;

    const alreadySelected = tempSelectedItems.some((f) => f.ind_id === item.ind_id);

    if (alreadySelected) {
      setTempSelectedItems((prev) => prev.filter((f) => f.ind_id !== item.ind_id));
    } else {
      if (tempSelectedItems.length >= 3) return;
      setTempSelectedItems((prev) => [...prev, item]);
    }
  };

  useEffect(() => {
    setSelectedItems([lists[0]]);
    onChange?.([lists[0]]);
  }, [lists]);

  const handleApply = () => {
    const selectedIds = new Set(tempSelectedItems.map((f) => f.ind_id));

    const selected = lists.filter((item) => selectedIds.has(item.ind_id));

    const unselected = lists.filter((item) => !selectedIds.has(item.ind_id));

    setSelectedItems(tempSelectedItems);
    setSortedList([...selected, ...unselected]);
    setIsApplied(true);
    setIsOpen(false);
    onChange?.(tempSelectedItems);
  };

  const handleReset = () => {
    setTempSelectedItems([]);
    setSelectedItems([]);
    setSortedList(lists);
    setIsApplied(false);
    setIsOpen(false);
    onChange?.([]);
  };

  const hasChanges = useMemo(() => {
    const tempIds = tempSelectedItems.map((i) => i.ind_id).sort();
    const appliedIds = selectedItems.map((i) => i.ind_id).sort();
    return JSON.stringify(tempIds) !== JSON.stringify(appliedIds);
  }, [tempSelectedItems, selectedItems]);

  const displayList = useMemo(() => {
    if (!search.trim()) return sortedList;

    return sortedList.filter((item) => item.stkexchg?.toLowerCase().includes(search.toLowerCase()));
  }, [sortedList, search]);

  const selectedIds = new Set(selectedItems.map((f) => f.ind_id));

  const lastSelectedSortedId = useMemo(() => {
    if (!isApplied) return null;

    const selectedGroup = sortedList.filter((item) => selectedIds.has(item.ind_id));

    return selectedGroup.length ? selectedGroup[selectedGroup.length - 1].ind_id : null;
  }, [sortedList, selectedItems, isApplied]);

  const showSeparator = isOpen && isApplied && selectedItems.length > 0 && !hasChanges;

  const buttonLabel =
    selectedItems.length === 0
      ? "Select Benchmark"
      : selectedItems
          .map((item) => item.stkexchg)
          .filter(Boolean)
          .join(", ");

  return (
    <div className="relative z-10 w-full max-w-96 mx-auto mb-6">
      {/* Trigger */}
      <p className="mb-1 text-center text-2sm sm:text-base text-[#949494]">Add Benchmarks</p>
      <button ref={dropdownRef} onClick={handleOpen} className={`w-full py-2 px-3 rounded-lg text-left bg-white border font-medium flex items-center justify-between transition duration-300 ${isOpen ? "border-[#1a51aa66]" : "border-[#94949480] hover:border-[#949494d8]"}`}>
        <span className="truncate text-sm text-[#949494]">{buttonLabel}</span>

        <Icon.dropdownDownArrow className={`text-primaryBlack/50 transition duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      <CustomDropdown isOpen={isOpen} onClose={handleClose} excludeRefs={[dropdownRef]} useHeight className="bg-white rounded-lg overflow-hidden w-full mt-0.5 px-1.5 py-1.5">
        <label className="group w-full py-1 px-1.5 rounded-md text-left bg-white border border-[#949494] text-sm font-medium mb-2 flex items-center justify-between gap-1 hover:border-[#5a5a5a] text-black/60 focus-within:text-black/90 focus-within:border-[#5a5a5a] transition duration-300">
          <Icon.searchIcon2 className="w-4 h-4 shrink-0 text-[#949494] transition duration-300 group-hover:text-[#5a5a5a]" />

          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-transparent text-[#949494] placeholder:text-black/60" placeholder="Search & Add Benchmarks" />

          {search && (
            <button onClick={() => setSearch("")} className="ml-auto shrink-0">
              <Icon.crossIcon className="w-3 h-3 hover:text-primaryBlue" />
            </button>
          )}
        </label>

        <div className="max-h-60 overflow-y-auto scrollbar-sm space-y-0.5 pr-1">
          {displayList.map((item) => {
            const isSelected = tempSelectedItems.some((f) => f.ind_id === item.ind_id);

            const disableNewSelection = !isSelected && tempSelectedItems.length >= 3;

            const isLastSelected = item.ind_id === lastSelectedSortedId;

            return (
              <Fragment key={item.ind_id ?? 0}>
                <button type="button" disabled={disableNewSelection} onClick={() => toggleSelect(item)} className={`w-full text-2xs text-left px-2 py-1 transition flex gap-3 items-center rounded-xs ${isSelected ? "text-primaryBlue bg-[#e6ebfd]" : disableNewSelection ? "text-primaryBlack/40" : "text-primaryBlack hover:bg-[#e6ebfd]"}`}>
                  <input type="checkbox" checked={isSelected} readOnly className="accent-primaryBlue" />
                  <span className="truncate">{item.stkexchg}</span>
                </button>

                {showSeparator && isLastSelected && <div className="block w-full border-y border-t-transparent border-dashed border-primaryBlack/30 my-1.5 pointer-events-none" />}
              </Fragment>
            );
          })}
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-between gap-2 pt-1.5 border-t">
          <button type="button" onClick={handleReset} className="transition flex-1 py-1.5 text-2xs border rounded-md hover:bg-gray-100">
            Reset
          </button>

          <button type="button" onClick={handleApply} className="transition flex-1 py-1.5 text-2xs bg-primaryBlue text-white rounded-md hover:opacity-85">
            Apply
          </button>
        </div>
      </CustomDropdown>
    </div>
  );
}
